import React, {useEffect, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {
  convertLocationParamsToFetchParams,
  useObjectActivity,
} from 'app/activity/api';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import StateButton from 'app/general/widgets/StateButton';
import {useModelChangeSubscription} from 'app/subscriptions';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import DetailPageActivitySidebarItem from './DetailPageActivitySidebarItem';
import ExportActivityButton from './ExportActivityButton';

import type {ActivityType, TargetActionType} from 'app/activity/types/activity';

interface Props {
  title: string;
  objectId: number | string;
  contentType:
    | 'taskinstance'
    | 'procedure'
    | 'policy'
    | 'proceduregroup'
    | 'quiz';
  showExportButton: boolean;
  contentArgs: TargetActionType;
  toggleSidebar: () => void;
}

export const DetailPageActivitySidebar = ({
  title,
  objectId,
  contentType,
  showExportButton,
  contentArgs,
  toggleSidebar,
}: Props) => {
  const [page, setPage] = useState(1);
  const [activities, setActivities] = useState<Array<ActivityType>>([]);

  const {currentData, isLoading, isFetching, refetch} = useObjectActivity(
    convertLocationParamsToFetchParams({
      target_object_id: objectId,
      target_content_type: contentType,
      page,
    })
  );

  useEffect(() => {
    if (currentData != null) {
      // Remove last page and re-add fresh data.
      const insertIndex = (page - 1) * DEFAULT_PAGE_SIZE;

      // works because Load More only ever appends, no reverse direction possible.
      setActivities(
        activities.slice(0, insertIndex).concat(currentData.results)
      );
    }
  }, [currentData]);

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, anyRelatedMatch}) => {
      runWhenAny(
        message,
        [
          modelIdMatch(contentType, objectId),
          anyRelatedMatch({
            content_type: contentType,
            id: objectId,
          }),
        ],
        () => {
          setActivities([]);
          if (page === 1) {
            refetch();
          } else {
            setPage(1);
          }
        }
      );
    },
    [objectId, contentType]
  );

  return (
    <DocSidebarPagePanels
      title={title}
      loading={isLoading}
      footer={
        showExportButton ? (
          <ExportActivityButton contentType={contentType} objectId={objectId} />
        ) : null
      }
      onClose={toggleSidebar}
    >
      <StyledTimelineUL className="list-unstyled">
        {activities.map((activity) => (
          <li key={activity.id} className="p-0">
            <DetailPageActivitySidebarItem
              activity={activity}
              contentArgs={contentArgs}
            />
          </li>
        ))}
      </StyledTimelineUL>
      {(currentData != null && currentData.next != null) ||
      (page > 1 && isFetching) ? (
        <div className="text-center my-2 mx-4">
          <StateButton
            isIdle={!isFetching}
            className="btn btn-secondary btn-sm btn-block"
            busyText={gt('Loading...')}
            onClick={() => setPage(page + 1)}
          >
            {gt('Load more')}
          </StateButton>
        </div>
      ) : null}
    </DocSidebarPagePanels>
  );
};

const StyledTimelineUL = styled.ul`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 20px;
    left: 19px;
    bottom: 20px;
    width: 2px;
    background: ${colors.linkBlue};
  }

  li:last-child {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 20px;
      left: 19px;
      bottom: 20px;
      width: 2px;
      background: #fff;
    }
  }
`;
